import type { FC } from "react";
import { useLayoutEffect, useMemo, useState } from "react";

import type { Options } from "highcharts";

import { ColumnChartGraph } from "@similarweb/pro-logic-components";

import type { LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import { getGraphConfig } from "./config";
import { getSharedLegendProps, VIEW_MODE_CONFIG } from "./consts";
import { useLegendSelection } from "./hooks/useLegendSelection";
import type { EViewMode, SeriesData, SeriesItem } from "./types";

interface ICompareChartSectionProps {
    transformedData: Record<EViewMode.Visibility | EViewMode.Sentiment, SeriesData> | undefined;
    selectedTopic: string;
    models: LLModel[];
    selectedViewMode: EViewMode.Visibility | EViewMode.Sentiment;
}

const legendValueFilter = (viewMode: EViewMode.Visibility | EViewMode.Sentiment) =>
    VIEW_MODE_CONFIG[viewMode]?.displayValueFilter ?? ((value: number) => value.toString());

export const CompareChartSection: FC<ICompareChartSectionProps> = ({
    transformedData,
    selectedTopic,
    models,
    selectedViewMode,
}) => {
    const [mutableTransformedData, setMutableTransformedData] = useState(transformedData);

    useLayoutEffect(() => {
        setMutableTransformedData(transformedData);
    }, [transformedData]);

    const topicFilteredData = useMemo<SeriesItem[]>(() => {
        const lowercaseTopicName = selectedTopic.toLowerCase();
        const viewModeData = mutableTransformedData?.[selectedViewMode];
        return viewModeData
            ? (Object.entries(viewModeData).find(([key]) => key.toLowerCase() === lowercaseTopicName)?.[1] ?? [])
            : [];
    }, [mutableTransformedData, selectedTopic, selectedViewMode]);

    const { onLegendSelection } = useLegendSelection({
        topicFilteredData,
        selectedTopic,
        selectedViewMode,
        setTransformedData: setMutableTransformedData,
    });

    const graphConfig = useMemo(
        () => getGraphConfig(topicFilteredData, selectedViewMode, models) as Options,
        [topicFilteredData, selectedViewMode, models],
    );

    const coreAverageIndex = topicFilteredData.findIndex((item) => item.isCoreAverage);
    const separatorAboveIndex = coreAverageIndex >= 0 ? coreAverageIndex : null;

    return (
        <ColumnChartGraph<SeriesItem>
            key={`model-comparison-column-${selectedViewMode}`}
            graphConfig={graphConfig}
            data={topicFilteredData}
            stackColumns={false}
            legendValueFilter={legendValueFilter(selectedViewMode)}
            legendToggleHandlerCallback={onLegendSelection}
            presentLineSeparatorAboveItemIndex={separatorAboveIndex}
            {...getSharedLegendProps()}
        />
    );
};
